import { useCallback, useEffect, useMemo } from "react";
import { generateTimeSlots, getAvailableEndTimes, getDefaultEndTime, slotSizeToDurationHours } from "@/utils/timeUtils";

interface UseTimeSlotPickerProps {
  startTime: string;
  onStartTimeChange: (value: string) => void;
  endTime?: string; // Make this optional
  onEndTimeChange: (value: string) => void;
  selectedTimeSlot: string;
  serviceSlotSize?: string; // Add service slot size prop
  startHour?: number;
  endHour?: number;
  interval?: 30 | 60;
}

export const useTimeSlotPicker = ({
  startTime,
  onStartTimeChange,
  endTime,
  onEndTimeChange,
  selectedTimeSlot,
  serviceSlotSize = "30", // Default to 30 minutes
  startHour = 8,  // Updated default to 8am
  endHour = 20,   // 8pm (20:00)
  interval = 30
}: UseTimeSlotPickerProps) => {
  // Generate time slots based on props
  const timeSlots = useMemo(() => {
    const slots = generateTimeSlots(startHour, endHour, interval);
    console.log('Generated time slots:', slots);
    return slots;
  }, [startHour, endHour, interval]);
  
  // Generate available end times based on the selected start time
  const availableEndTimes = useMemo(() => {
    const endTimes = getAvailableEndTimes(startTime, timeSlots);
    console.log('Available end times for', startTime, ':', endTimes);
    return endTimes;
  }, [startTime, timeSlots]);

  // Handle start time change and set default end time
  const handleStartTimeChange = useCallback((newStartTime: string) => {
    console.log('handleStartTimeChange called with:', newStartTime);
    onStartTimeChange(newStartTime);
    
    // Use service's slot size to calculate default end time
    const durationHours = slotSizeToDurationHours(serviceSlotSize);
    const defaultEndTime = getDefaultEndTime(newStartTime, durationHours);
    console.log('Default end time calculated with slot size', serviceSlotSize, ':', defaultEndTime);
    
    if (defaultEndTime && timeSlots.includes(defaultEndTime)) {
      console.log('Setting end time to default:', defaultEndTime);
      onEndTimeChange(defaultEndTime);
    } else if (availableEndTimes.length > 0) {
      // If the default end time is not available, select the first available end time
      console.log('Default end time not available, using first available:', availableEndTimes[0]);
      onEndTimeChange(availableEndTimes[0]);
    } else {
      console.warn('No available end times found for start time:', newStartTime);
    }
  }, [onStartTimeChange, onEndTimeChange, timeSlots, availableEndTimes, serviceSlotSize]);

  // Initialize time selection with the selected time slot
  useEffect(() => {
    console.log('useTimeSlotPicker effect - selectedTimeSlot:', selectedTimeSlot);
    console.log('useTimeSlotPicker effect - startTime:', startTime);
    console.log('useTimeSlotPicker effect - timeSlots available:', timeSlots);
    
    if (selectedTimeSlot && (!startTime || startTime !== selectedTimeSlot)) {
      console.log('Setting start time to selected time slot:', selectedTimeSlot);
      handleStartTimeChange(selectedTimeSlot);
    }
  }, [selectedTimeSlot, startTime, handleStartTimeChange, timeSlots]);

  return {
    timeSlots,
    availableEndTimes,
    handleStartTimeChange
  };
};